/**
 * Decide whether to auto-start the onboarding mentor on EditorPage mount,
 * and what branch ("blank" vs "template") to start in.
 *
 * Recommended policy from Phase 4 close-out:
 *   1. If the URL has `?onboarding_template=<id>` (set by NewPagePage on
 *      creation), trust it. id === "blank" → blank branch, else template.
 *   2. Otherwise fall back to inspecting the page's initial Puck data:
 *      an empty content array means the user is on a blank canvas, so
 *      blank-branch auto-start is safe.
 *   3. Otherwise do nothing — the user is presumably editing an existing
 *      page and would find an auto-start intrusive. They can re-open via
 *      the Profile page restart card (Phase 6).
 */

import type { Data } from "@puckeditor/core";

export interface AutoStartDecision {
  shouldStart: boolean;
  branch: "blank" | "template";
  templateId: string | null;
}

export const NEW_PAGE_TEMPLATE_PARAM = "onboarding_template";
export const BLANK_TEMPLATE_SENTINEL = "blank";

export function decideAutoStart(input: {
  search: string;
  data: Data | null;
}): AutoStartDecision {
  const params = new URLSearchParams(input.search);
  const param = params.get(NEW_PAGE_TEMPLATE_PARAM);

  if (param) {
    if (param === BLANK_TEMPLATE_SENTINEL) {
      return { shouldStart: true, branch: "blank", templateId: null };
    }
    return { shouldStart: true, branch: "template", templateId: param };
  }

  // No URL hint — only auto-start on a truly empty page so we don't
  // ambush returning editors.
  const content = input.data?.content;
  if (Array.isArray(content) && content.length === 0) {
    return { shouldStart: true, branch: "blank", templateId: null };
  }

  return { shouldStart: false, branch: "blank", templateId: null };
}

/**
 * Strip the onboarding param from the URL after we've consumed it, so
 * a refresh doesn't re-trigger if the user has skipped. Uses
 * history.replaceState — no React Router coupling.
 */
export function clearOnboardingParam(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(NEW_PAGE_TEMPLATE_PARAM)) return;
    url.searchParams.delete(NEW_PAGE_TEMPLATE_PARAM);
    window.history.replaceState({}, "", url.toString());
  } catch {
    // ignore — non-fatal
  }
}
